import type { CarSnapshot, Gear, SimEvent } from '../sim/types';
import { isForwardGear } from '../sim/types';
import type { LevelDef, ObjectiveDef } from './types';
import { pointInZone, projectOnPath, type ZoneProp } from './geometry';

/**
 * Evaluador de nivel: consume snapshots + eventos cada tick y produce
 * métricas de instructor (suavidad, observación, normas, velocidad).
 * Nunca premia la velocidad.
 */

export type ObjectiveStatus = 'pending' | 'active' | 'done';

export interface ObjectiveUiState {
  id: string;
  titleKey: string;
  status: ObjectiveStatus;
  /** Requisitos aún no satisfechos (claves i18n de pista). */
  hints: string[];
}

export interface EvaluatorUiState {
  objectives: ObjectiveUiState[];
  activeIndex: number;
  timeS: number;
  collisions: number;
  finished: boolean;
  failedKey: string | null;
}

export interface AttemptMetrics {
  smoothness: number;
  observation: number;
  ruleCompliance: number;
  speedCompliance: number;
  collisions: number;
  harshAccels: number;
  harshBrakes: number;
  timeS: number;
  score: number;
  stars: 0 | 1 | 2 | 3;
  /** MANUAL: nº de caladas del intento (0 en automáticos). */
  stalls: number;
  /** MANUAL: control del embrague 0-100 (patinado, calor, rascadas). */
  clutchScore: number;
  /** MANUAL: elección de marcha 0-100 (ni ahogado ni pasado de vueltas). */
  gearScore: number;
  /** MANUAL: retroceso máximo en arranques en cuesta (m). */
  rollbackM: number;
}

export interface AttemptResult {
  levelId: string;
  passed: boolean;
  metrics: AttemptMetrics;
  /** Claves i18n de los fallos detectados (para el análisis del instructor). */
  failures: string[];
  /** Claves i18n de consejos según lo observado. */
  tips: string[];
  inputLog: number[][];
}

interface ObjectiveRuntime {
  def: ObjectiveDef;
  status: ObjectiveStatus;
  /** Ticks acumulados con el intermitente requerido encendido. */
  signalTicks: number;
  /** Ticks con exceso sobre require.maxSpeedKmh. */
  overSpeedTicks: number;
  sustainTicks: number;
  requireMissed: Set<string>;
  /** hill_start: posición y rumbo al detenerse en la zona. */
  hillStop: { x: number; z: number; fx: number; fz: number } | null;
  hillRollbackM: number;
  hillWarned: boolean;
}

const HARSH_ACCEL = 3.0; // m/s²
const HARSH_BRAKE = -4.0;
const GLANCE_WINDOW_TICKS = 300; // 5 s de validez de una mirada al espejo
const MAX_LOG_TICKS = 36000; // 10 min

/** Codifica la marcha para el registro compacto del replay. */
function gearToNum(g: Gear): number {
  if (isForwardGear(g)) return 3 + g; // 1..6 → 4..9
  return { P: 0, R: 1, N: 2, D: 3 }[g];
}

export class Evaluator {
  readonly level: LevelDef;
  private objectives: ObjectiveRuntime[];
  private zones: ZoneProp[];
  private tick = 0;
  private collisions = 0;
  private coneHits = 0;
  private harshAccels = 0;
  private harshBrakes = 0;
  private harshArm = true;
  private harshHoldTicks = 0;
  private ignoreHarshUntil = 0;
  private lastGlance: Record<'left' | 'right' | 'rear', number> = {
    left: -99999,
    right: -99999,
    rear: -99999,
  };
  private prevGlance: CarSnapshot['glance'] = 'none';
  private speedZoneTicks = 0;
  private speedZoneOkTicks = 0;
  private beltViolationTicks = 0;
  private offPathTicks = 0;
  private missedSignals = 0;
  private missedGlances = 0;
  private missedSpeedReqs = 0;
  private inputLog: number[][] = [];
  private failedKey: string | null = null;
  private doneAll = false;

  // --- Métricas específicas del cambio manual ---
  private stalls = 0;
  private grinds = 0;
  private overRevs = 0;
  private clutchHeatPeak = 0;
  private heatWarned = false;
  private luggingTicks = 0;
  private luggingWarnCooldown = 0;
  private gearOkTicks = 0;
  private gearBadTicks = 0;
  private rollbackMaxM = 0;
  private rollbackFailed = false;

  constructor(level: LevelDef) {
    this.level = level;
    this.objectives = level.objectives.map((def, i) => ({
      def,
      status: i === 0 ? 'active' : 'pending',
      signalTicks: 0,
      overSpeedTicks: 0,
      sustainTicks: 0,
      requireMissed: new Set(),
      hillStop: null,
      hillRollbackM: 0,
      hillWarned: false,
    }));
    this.zones = level.environment.props.filter((p): p is ZoneProp => p.type === 'zone');
  }

  get finished(): boolean {
    return this.doneAll || this.failedKey !== null;
  }

  private zone(id: string): ZoneProp | undefined {
    return this.zones.find((z) => z.id === id);
  }

  /** Posición del morro del coche. */
  private frontPos(snap: CarSnapshot): [number, number] {
    const q = snap.quat;
    const fx = 2 * (q.x * q.z + q.w * q.y);
    const fz = 1 - 2 * (q.x * q.x + q.y * q.y);
    const half = 2.2;
    return [snap.pos.x + fx * half, snap.pos.z + fz * half];
  }
  private rearPos(snap: CarSnapshot): [number, number] {
    const [fx, fz] = this.frontPos(snap);
    return [2 * snap.pos.x - fx, 2 * snap.pos.z - fz];
  }

  /** Procesa un tick. Devuelve claves i18n de avisos nuevos (para toasts). */
  tickUpdate(snap: CarSnapshot, events: SimEvent[]): string[] {
    if (this.finished) return [];
    this.tick++;
    const warnings: string[] = [];
    const level = this.level;

    // Registro de entradas para replay
    if (this.inputLog.length < MAX_LOG_TICKS) {
      this.inputLog.push([
        Math.round(snap.throttle * 100) / 100,
        Math.round(snap.brake * 100) / 100,
        Math.round(snap.steerRad * 1000) / 1000,
        gearToNum(snap.gear),
        Math.round((snap.manual?.clutchPedal ?? 0) * 100) / 100,
      ]);
    }

    // Miradas a espejos
    if (snap.glance !== 'none' && snap.glance !== this.prevGlance) {
      this.lastGlance[snap.glance] = this.tick;
    }
    this.prevGlance = snap.glance;

    // Colisiones y eventos del tren motriz manual
    for (const ev of events) {
      if (ev.type === 'collision') {
        if (ev.tag === 'cone') this.coneHits++;
        this.collisions++;
        this.ignoreHarshUntil = this.tick + 90;
        warnings.push(ev.tag === 'cone' ? 'warn.coneHit' : 'warn.collision');
      } else if (ev.type === 'stall') {
        this.stalls++;
        this.ignoreHarshUntil = this.tick + 60; // el tirón del calado no es "frenazo"
        warnings.push('warn.stall');
      } else if (ev.type === 'gearGrind') {
        this.grinds++;
        warnings.push('warn.grind');
      } else if (ev.type === 'overRev') {
        this.overRevs++;
        warnings.push('warn.overRev');
      }
    }

    // Telemetría manual continua: calor de embrague y elección de marcha
    const man = snap.manual;
    if (man) {
      this.clutchHeatPeak = Math.max(this.clutchHeatPeak, man.clutchHeat);
      if (man.clutchHeat > 0.6 && !this.heatWarned) {
        this.heatWarned = true;
        warnings.push('warn.clutchHeat');
      }
      if (man.clutchHeat < 0.35) this.heatWarned = false;

      this.luggingWarnCooldown = Math.max(0, this.luggingWarnCooldown - 1);
      if (man.lugging) {
        this.luggingTicks++;
        if (this.luggingTicks % 45 === 30 && this.luggingWarnCooldown === 0) {
          this.luggingWarnCooldown = 240;
          warnings.push('warn.lugging');
        }
      }
      if (snap.speedKmh > 7 && man.engineOn) {
        const badGear = man.lugging || man.overRev;
        if (badGear) this.gearBadTicks++;
        else this.gearOkTicks++;
      }
    }

    // Suavidad: acelerones y frenazos sostenidos
    if (this.tick > this.ignoreHarshUntil) {
      const a = snap.accelLong;
      if (a > HARSH_ACCEL || a < HARSH_BRAKE) {
        this.harshHoldTicks++;
        if (this.harshHoldTicks >= 12 && this.harshArm) {
          if (a > 0) {
            this.harshAccels++;
            warnings.push('warn.harshAccel');
          } else {
            this.harshBrakes++;
            warnings.push('warn.harshBrake');
          }
          this.harshArm = false;
        }
      } else if (Math.abs(a) < 2.0) {
        this.harshHoldTicks = 0;
        this.harshArm = true;
      }
    }

    // Cinturón
    if (!snap.seatbelt && snap.speedKmh > 5) {
      this.beltViolationTicks++;
      if (this.beltViolationTicks === 90) warnings.push('warn.seatbelt');
    }

    // Zonas de velocidad
    for (const z of this.zones) {
      if (z.kind === 'speed' && z.limitKmh && pointInZone(z, snap.pos.x, snap.pos.z)) {
        this.speedZoneTicks++;
        if (snap.speedKmh <= z.limitKmh + 3) this.speedZoneOkTicks++;
        else if (this.speedZoneTicks % 120 === 0) warnings.push('warn.speeding');
      }
    }

    // Objetivo activo
    const idx = this.objectives.findIndex((o) => o.status === 'active');
    if (idx >= 0) {
      const obj = this.objectives[idx]!;
      this.updateObjective(obj, snap, warnings);
      if (obj.status === 'done') {
        this.finalizeRequire(obj);
        const next = this.objectives[idx + 1];
        if (next) next.status = 'active';
        else this.doneAll = true;
      }
    }

    // Fallos duros
    if (this.collisions > level.pass.maxCollisions) {
      this.failedKey = 'fail.collisions';
    } else if (level.pass.maxTimeS && this.timeS > level.pass.maxTimeS) {
      this.failedKey = 'fail.timeout';
    }
    return warnings;
  }

  private requireHints(obj: ObjectiveRuntime, snap: CarSnapshot): string[] {
    const man = snap.manual;
    // El motor calado domina cualquier otra pista
    if (man && !man.engineOn) {
      return [man.clutchPedal < 0.7 && snap.gear !== 'N' ? 'hint.clutchToStart' : 'hint.restart'];
    }
    const hints: string[] = [];
    const req = obj.def.require;
    if (req) {
      if (req.signal) {
        const on = req.signal === 'left' ? snap.signalLeft : snap.signalRight;
        if (!on && obj.signalTicks < 45) hints.push(`hint.signal.${req.signal}`);
      }
      for (const dir of req.glance ?? []) {
        if (this.tick - this.lastGlance[dir] > GLANCE_WINDOW_TICKS) hints.push(`hint.glance.${dir}`);
      }
      if (req.maxSpeedKmh && snap.speedKmh > req.maxSpeedKmh) hints.push('hint.slowDown');
    }
    hints.push(...this.manualHints(obj, snap));
    return hints;
  }

  /** Pistas de instructor para los objetivos del cambio manual. */
  private manualHints(obj: ObjectiveRuntime, snap: CarSnapshot): string[] {
    const man = snap.manual;
    if (!man) return [];
    const def = obj.def;
    const hints: string[] = [];
    switch (def.type) {
      case 'shift_to': {
        if (snap.gear !== def.gear && man.clutchPedal < 0.6) hints.push('hint.clutchToShift');
        break;
      }
      case 'bite_hold': {
        if (!isForwardGear(snap.gear)) hints.push('hint.gearFirst');
        else if (snap.handbrake) hints.push('hint.handbrakeOff');
        else if (snap.brake > 0.15) hints.push('hint.releaseBrake');
        else if (!man.atBite)
          hints.push(man.clutchPedal > 0.62 ? 'hint.easeClutchOut' : 'hint.moreClutch');
        break;
      }
      case 'move_off': {
        if (!isForwardGear(snap.gear)) hints.push('hint.gearFirst');
        else if (snap.handbrake) hints.push('hint.handbrakeOff');
        else if (snap.speedKmh < 2 && man.clutchPedal > 0.85 && snap.throttle < 0.05)
          hints.push('hint.throttleAndEase');
        break;
      }
      case 'hill_start': {
        if (obj.hillStop === null) {
          hints.push('hint.stopOnSlope');
        } else if (Math.abs(snap.speedMs) < 0.5 && !snap.handbrake && snap.brake < 0.1) {
          hints.push('hint.useHandbrake');
        } else if (snap.handbrake && man.atBite) {
          hints.push('hint.releaseHandbrake');
        }
        break;
      }
      default:
        break;
    }
    return hints;
  }

  private trackRequire(obj: ObjectiveRuntime, snap: CarSnapshot): void {
    const req = obj.def.require;
    if (!req) return;
    if (req.signal) {
      const on = req.signal === 'left' ? snap.signalLeft : snap.signalRight;
      if (on) obj.signalTicks++;
    }
    if (req.maxSpeedKmh && snap.speedKmh > req.maxSpeedKmh + 2) obj.overSpeedTicks++;
  }

  /** Al completar un objetivo, consolida qué requisitos se incumplieron. */
  private finalizeRequire(obj: ObjectiveRuntime): void {
    const req = obj.def.require;
    if (!req) return;
    if (req.signal && obj.signalTicks < 45) {
      obj.requireMissed.add(`signal.${req.signal}`);
      this.missedSignals++;
    }
    for (const dir of req.glance ?? []) {
      if (this.tick - this.lastGlance[dir] > GLANCE_WINDOW_TICKS) {
        obj.requireMissed.add(`glance.${dir}`);
        this.missedGlances++;
      }
    }
    if (req.maxSpeedKmh && obj.overSpeedTicks > 90) {
      obj.requireMissed.add('speed');
      this.missedSpeedReqs++;
    }
  }

  private updateObjective(obj: ObjectiveRuntime, snap: CarSnapshot, warnings: string[]): void {
    this.trackRequire(obj, snap);
    const def = obj.def;
    switch (def.type) {
      case 'shift_to': {
        if (snap.gear === def.gear) obj.status = 'done';
        break;
      }
      case 'reach_speed': {
        if (snap.speedKmh >= def.minKmh) {
          obj.sustainTicks++;
          if (obj.sustainTicks >= 20) obj.status = 'done';
        } else obj.sustainTicks = 0;
        break;
      }
      case 'full_stop': {
        const stopped = snap.speedKmh < 0.8;
        let inPlace = true;
        if (def.zoneId) {
          const z = this.zone(def.zoneId);
          if (z) {
            const [fx, fz] = this.frontPos(snap);
            inPlace = pointInZone(z, fx, fz, 0.35);
          }
        }
        if (stopped && inPlace) {
          obj.sustainTicks++;
          if (obj.sustainTicks >= 45) obj.status = 'done';
        } else obj.sustainTicks = 0;
        break;
      }
      case 'drive_to_zone': {
        const z = this.zone(def.zoneId);
        if (z && pointInZone(z, snap.pos.x, snap.pos.z)) obj.status = 'done';
        break;
      }
      case 'follow_path': {
        const path = this.level.environment.paths?.[def.pathId];
        if (path && snap.speedKmh > 2) {
          const proj = projectOnPath(path, snap.pos.x, snap.pos.z);
          if (proj.lateralM > def.maxLateralM) {
            this.offPathTicks++;
            if (this.offPathTicks % 90 === 45) warnings.push('warn.laneDeparture');
          }
        }
        const z = this.zone(def.toZoneId);
        if (z && pointInZone(z, snap.pos.x, snap.pos.z)) obj.status = 'done';
        break;
      }
      case 'reverse_to_zone': {
        const z = this.zone(def.zoneId);
        if (z && snap.gear === 'R') {
          const [rx, rz] = this.rearPos(snap);
          if (pointInZone(z, rx, rz, 0.3) && pointInZone(z, snap.pos.x, snap.pos.z, 2.6)) {
            if (snap.speedKmh < 0.8) {
              obj.sustainTicks++;
              if (obj.sustainTicks >= 30) obj.status = 'done';
            } else obj.sustainTicks = 0;
          }
        }
        break;
      }
      case 'bite_hold': {
        // Coche casi quieto, sin frenos, sostenido solo por el punto de fricción
        const man = snap.manual;
        const holding =
          man !== null &&
          man.engineOn &&
          man.atBite &&
          isForwardGear(snap.gear) &&
          !snap.handbrake &&
          snap.brake < 0.15 &&
          Math.abs(snap.speedMs) < 0.55;
        if (holding) {
          obj.sustainTicks++;
          if (obj.sustainTicks >= Math.round(def.seconds * 60)) obj.status = 'done';
        } else if (obj.sustainTicks > 0) {
          obj.sustainTicks = Math.max(0, obj.sustainTicks - 3); // perdona parpadeos
        }
        break;
      }
      case 'move_off': {
        if (snap.speedKmh >= def.minKmh && snap.speedMs > 0) {
          obj.sustainTicks++;
          if (obj.sustainTicks >= 15) obj.status = 'done';
        } else obj.sustainTicks = 0;
        break;
      }
      case 'hill_start': {
        const z = this.zone(def.zoneId);
        if (!z) break;
        if (obj.hillStop === null) {
          // Fase 1: detenerse por completo dentro de la zona en pendiente
          if (pointInZone(z, snap.pos.x, snap.pos.z) && snap.speedKmh < 0.7) {
            obj.sustainTicks++;
            if (obj.sustainTicks >= 40) {
              const q = snap.quat;
              obj.hillStop = {
                x: snap.pos.x,
                z: snap.pos.z,
                fx: 2 * (q.x * q.z + q.w * q.y),
                fz: 1 - 2 * (q.x * q.x + q.y * q.y),
              };
              obj.sustainTicks = 0;
            }
          } else if (obj.sustainTicks > 0 && !pointInZone(z, snap.pos.x, snap.pos.z)) {
            obj.sustainTicks = 0;
          }
        } else {
          // Fase 2: salir hacia delante sin retroceder
          const hs = obj.hillStop;
          const back = (hs.x - snap.pos.x) * hs.fx + (hs.z - snap.pos.z) * hs.fz;
          if (back > obj.hillRollbackM) {
            obj.hillRollbackM = back;
            this.rollbackMaxM = Math.max(this.rollbackMaxM, back);
            if (back > def.maxRollbackM && !obj.hillWarned) {
              obj.hillWarned = true;
              obj.requireMissed.add('rollback');
              this.rollbackFailed = true;
              warnings.push('warn.rollback');
            }
          }
          if (snap.speedKmh >= def.minKmh && snap.speedMs > 0) {
            obj.sustainTicks++;
            if (obj.sustainTicks >= 15) obj.status = 'done';
          } else obj.sustainTicks = 0;
        }
        break;
      }
    }
  }

  get timeS(): number {
    return this.tick / 60;
  }

  uiState(snap: CarSnapshot | null): EvaluatorUiState {
    const activeIndex = this.objectives.findIndex((o) => o.status === 'active');
    return {
      objectives: this.objectives.map((o) => ({
        id: o.def.id,
        titleKey: o.def.titleKey,
        status: o.status,
        hints: o.status === 'active' && snap ? this.requireHints(o, snap) : [],
      })),
      activeIndex,
      timeS: this.timeS,
      collisions: this.collisions,
      finished: this.finished,
      failedKey: this.failedKey,
    };
  }

  /** Calcula el resultado final del intento. */
  finish(): AttemptResult {
    const level = this.level;
    const pass = level.pass;

    const smoothness = Math.max(
      0,
      100 - this.harshAccels * 16 - this.harshBrakes * 16 - Math.min(20, this.offPathTicks / 30),
    );
    const totalGlanceReqs = level.objectives.reduce(
      (n, o) => n + (o.require?.glance?.length ?? 0),
      0,
    );
    const observation =
      totalGlanceReqs === 0
        ? 100
        : Math.max(0, Math.round(100 * (1 - this.missedGlances / totalGlanceReqs)));
    const totalSignalReqs = level.objectives.reduce((n, o) => n + (o.require?.signal ? 1 : 0), 0);
    const beltPenalty = this.beltViolationTicks > 90 ? 30 : 0;
    const signalPenalty =
      totalSignalReqs === 0 ? 0 : Math.round(50 * (this.missedSignals / totalSignalReqs));
    const ruleCompliance = Math.max(
      0,
      100 - signalPenalty - beltPenalty - this.missedSpeedReqs * 15 - this.coneHits * 10,
    );
    const speedCompliance =
      this.speedZoneTicks === 0 ? 1 : this.speedZoneOkTicks / this.speedZoneTicks;

    const collisionScore = Math.max(0, 100 - this.collisions * 40);

    // Métricas del cambio manual
    const isManual = level.track === 'manual';
    const totalGearTicks = this.gearOkTicks + this.gearBadTicks;
    const gearScore =
      totalGearTicks === 0 ? 100 : Math.round((this.gearOkTicks / totalGearTicks) * 100);
    const clutchScore = Math.max(
      0,
      Math.round(100 - this.clutchHeatPeak * 55 - this.grinds * 8 - this.overRevs * 6),
    );
    const stallPenalty = Math.min(30, this.stalls * 6);

    const score = isManual
      ? Math.max(
          0,
          Math.round(
            smoothness * 0.22 +
              observation * 0.13 +
              ruleCompliance * 0.12 +
              speedCompliance * 100 * 0.05 +
              collisionScore * 0.15 +
              clutchScore * 0.18 +
              gearScore * 0.15 -
              stallPenalty,
          ),
        )
      : Math.round(
          smoothness * 0.3 +
            observation * 0.25 +
            ruleCompliance * 0.2 +
            speedCompliance * 100 * 0.1 +
            collisionScore * 0.15,
        );

    const failures: string[] = [];
    if (this.failedKey) failures.push(this.failedKey);
    if (!this.doneAll && !this.failedKey) failures.push('fail.incomplete');
    if (this.harshAccels > pass.maxHarshAccel) failures.push('fail.harshAccel');
    if (this.harshBrakes > pass.maxHarshBrake) failures.push('fail.harshBrake');
    if (pass.minObservationScore && observation < pass.minObservationScore)
      failures.push('fail.observation');
    if (pass.speedComplianceMin && speedCompliance < pass.speedComplianceMin)
      failures.push('fail.speeding');
    if (pass.maxStalls !== undefined && this.stalls > pass.maxStalls) failures.push('fail.stalls');
    if (pass.minGearScore && gearScore < pass.minGearScore) failures.push('fail.gearScore');
    if (pass.minClutchScore && clutchScore < pass.minClutchScore)
      failures.push('fail.clutchScore');
    if (this.rollbackFailed) failures.push('fail.rollback');
    if (pass.minOverallScore && score < pass.minOverallScore) failures.push('fail.lowScore');

    const tips: string[] = [];
    if (this.stalls > 0) tips.push('tip.stall');
    if (this.rollbackFailed) tips.push('tip.hillStart');
    if (this.clutchHeatPeak > 0.5) tips.push('tip.clutchHeat');
    if (this.grinds > 0) tips.push('tip.grind');
    if (isManual && gearScore < 70) tips.push('tip.gearChoice');
    if (this.harshBrakes > 0) tips.push('tip.brakeEarlier');
    if (this.harshAccels > 0) tips.push('tip.progressiveThrottle');
    if (this.missedGlances > 0) tips.push('tip.mirrors');
    if (this.missedSignals > 0) tips.push('tip.signals');
    if (this.coneHits > 0) tips.push('tip.references');
    if (this.beltViolationTicks > 90) tips.push('tip.seatbelt');
    if (tips.length === 0) tips.push(isManual ? 'tip.greatManual' : 'tip.great');

    const passed = failures.length === 0;
    const stars: 0 | 1 | 2 | 3 = !passed ? 0 : score >= 85 ? 3 : score >= 70 ? 2 : 1;

    return {
      levelId: level.id,
      passed,
      metrics: {
        smoothness: Math.round(smoothness),
        observation,
        ruleCompliance: Math.round(ruleCompliance),
        speedCompliance: Math.round(speedCompliance * 100) / 100,
        collisions: this.collisions,
        harshAccels: this.harshAccels,
        harshBrakes: this.harshBrakes,
        timeS: Math.round(this.timeS * 10) / 10,
        score,
        stars,
        stalls: this.stalls,
        clutchScore: isManual ? clutchScore : 100,
        gearScore: isManual ? gearScore : 100,
        rollbackM: Math.round(this.rollbackMaxM * 100) / 100,
      },
      failures,
      tips,
      inputLog: this.inputLog,
    };
  }
}
